import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

export default function UserDetails() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken, isSignedIn, isLoaded } = useAuth();
  
  // Form data for user details
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    business_name: '',
    street_address_1: '',
    street_address_2: '',
    city: '',
    state_province: '',
    country: 'United States',
    zip_postal_code: '',
    mobile_phone: '',
    notification_preference: 'email'
  });

  // Mobile verification state
  const [mobileVerification, setMobileVerification] = useState({
    isVerified: false,
    codeSent: false,
    verificationCode: '',
    isVerifying: false,
    isSendingCode: false,
    canResend: true,
    resendTimer: 0
  });

  // Check authentication and existing user details
  useEffect(() => {
    const checkAuthAndUserDetails = async () => {
      if (!isLoaded) return;
      
      if (!isSignedIn) {
        navigate('/sign-in');
        return;
      }

      try {
        setIsLoading(true);
        const token = await getToken();
        
        // Check if user already has details filled out
        const response = await fetch('https://parrotmate.com/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const userData = await response.json();
        console.log('API Response:', userData); // Debug log
        
        // Extract user data from the nested structure
        const user = userData.user || {};
        
        // If user details are already complete, redirect to onboarding or plans
        if (user.street_address_1 && user.city && user.state_province && user.country && user.zip_postal_code) {
          // Check if user has subscription
          if (!userData.billing_preferences) {
            navigate('/plans');
          } else {
            // Pass along the plan parameter if it exists
            const selectedPlan = searchParams.get('plan');
            const onboardingUrl = selectedPlan ? `/onboarding?plan=${selectedPlan}` : '/onboarding';
            navigate(onboardingUrl);
          }
          return;
        }

        // Pre-fill form with any existing data
        setFormData({
          email: user.email || '',
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          business_name: user.business_name || '',
          street_address_1: user.street_address_1 || '',
          street_address_2: user.street_address_2 || '',
          city: user.city || '',
          state_province: user.state_province || '',
          country: user.country || 'United States',
          zip_postal_code: user.zip_postal_code || '',
          mobile_phone: user.mobile_phone || '',
          notification_preference: user.notification_preference || 'email'
        });

        // Set mobile verification status
        setMobileVerification(prev => ({
          ...prev,
          isVerified: user.mobile_phone_verified || false
        }));

      } catch (error) {
        console.error('Failed to check user details:', error);
        setError('Failed to load user information');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthAndUserDetails();
  }, [isLoaded, isSignedIn, getToken, navigate, searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // If SMS is selected but no mobile phone is provided, switch to email
      if (name === 'notification_preference' && value === 'sms' && !prev.mobile_phone) {
        return {
          ...newData,
          notification_preference: 'email'
        };
      }
      
      return newData;
    });
  };

  // Mobile phone verification functions
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (digits.length >= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length >= 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return digits;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({
      ...prev,
      mobile_phone: formatted
    }));
    
    // Reset verification status if phone number changes
    if (mobileVerification.isVerified) {
      setMobileVerification(prev => ({
        ...prev,
        isVerified: false,
        codeSent: false,
        verificationCode: ''
      }));
    }
  };

  const sendVerificationCode = async () => {
    if (!formData.mobile_phone || mobileVerification.isSendingCode) return;
    
    // Extract digits only for API call
    const phoneDigits = formData.mobile_phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setMobileVerification(prev => ({ ...prev, isSendingCode: true }));
    setError(null);

    try {
      const token = await getToken();
      const response = await fetch('https://parrotmate.com/api/user/mobile/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          mobile_phone: `+1${phoneDigits}`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send verification code');
      }

      setMobileVerification(prev => ({
        ...prev,
        codeSent: true,
        canResend: false,
        resendTimer: 120 // 2 minutes = 120 seconds
      }));

      // Start resend timer
      const timer = setInterval(() => {
        setMobileVerification(prev => {
          if (prev.resendTimer <= 1) {
            clearInterval(timer);
            return { ...prev, canResend: true, resendTimer: 0 };
          }
          return { ...prev, resendTimer: prev.resendTimer - 1 };
        });
      }, 1000);

    } catch (error) {
      console.error('Failed to send verification code:', error);
      setError(error.message);
    } finally {
      setMobileVerification(prev => ({ ...prev, isSendingCode: false }));
    }
  };

  const verifyCode = async () => {
    if (!mobileVerification.verificationCode || mobileVerification.isVerifying) return;

    setMobileVerification(prev => ({ ...prev, isVerifying: true }));
    setError(null);

    try {
      const token = await getToken();
      
      // Extract digits only for API call
      const phoneDigits = formData.mobile_phone.replace(/\D/g, '');
      
      const response = await fetch('https://parrotmate.com/api/user/mobile/confirm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          mobile_phone: `+1${phoneDigits}`,
          verification_code: mobileVerification.verificationCode
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Invalid verification code');
      }

      setMobileVerification(prev => ({
        ...prev,
        isVerified: true,
        codeSent: false,
        verificationCode: ''
      }));

    } catch (error) {
      console.error('Failed to verify code:', error);
      setError(error.message);
    } finally {
      setMobileVerification(prev => ({ ...prev, isVerifying: false }));
    }
  };

  const validateForm = () => {
    const required = ['first_name', 'last_name', 'street_address_1', 'city', 'state_province', 'country', 'zip_postal_code'];
    for (const field of required) {
      if (!formData[field].trim()) {
        return false;
      }
    }
    
    // If mobile phone is provided, it must be verified
    if (formData.mobile_phone && !mobileVerification.isVerified) {
      return false;
    }
    
    // If SMS notification is selected, mobile phone must be provided and verified
    if (formData.notification_preference === 'sms' && (!formData.mobile_phone || !mobileVerification.isVerified)) {
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await getToken();
      
      // Only send the fields that can be updated (exclude email)
      const updateData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        business_name: formData.business_name,
        street_address_1: formData.street_address_1,
        street_address_2: formData.street_address_2,
        city: formData.city,
        state_province: formData.state_province,
        country: formData.country,
        zip_postal_code: formData.zip_postal_code,
        notification_preference: formData.notification_preference
      };

      // Include mobile phone if provided and verified
      if (formData.mobile_phone && mobileVerification.isVerified) {
        // Convert formatted phone to E.164 format for storage
        const phoneDigits = formData.mobile_phone.replace(/\D/g, '');
        updateData.mobile_phone = `+1${phoneDigits}`;
      }
      
      const response = await fetch('https://parrotmate.com/api/update-profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user details');
      }

      // Success - redirect to plans or onboarding
      const selectedPlan = searchParams.get('plan');
      
      // Check if user has subscription
      const userResponse = await fetch('https://parrotmate.com/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (userResponse.ok) {
        const userData = await userResponse.json();
        if (!userData.billing_preferences) {
          navigate('/plans');
        } else {
          const onboardingUrl = selectedPlan ? `/onboarding?plan=${selectedPlan}` : '/onboarding';
          navigate(onboardingUrl);
        }
      } else {
        navigate('/plans');
      }

    } catch (error) {
      console.error('Failed to update user details:', error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading screen while checking authentication
  if (!isLoaded || isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Shared input styles to ensure visible, consistent text boxes
  const baseInputClasses =
    "w-full px-4 py-3 rounded-lg border border-gray-300 bg-white placeholder-gray-400 text-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto bg-teal-100 rounded-full flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h6m-6 4h6m-6 4h6" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 dark:text-white">
            Tell Us About You & Your Business
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            We need some basic information to set up your account and provide the best service.
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Address (Non-editable) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                readOnly
                className={`w-full px-4 py-3 rounded-lg border border-gray-300 bg-gray-50 text-gray-700 cursor-not-allowed dark:bg-gray-900 dark:text-gray-300 dark:border-gray-600`}
                placeholder="your.email@example.com"
              />
              <p className="text-sm text-gray-500 mt-1">
                Email address cannot be changed
              </p>
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className={baseInputClasses}
                  placeholder="John"
                  required
                  maxLength={50}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className={baseInputClasses}
                  placeholder="Smith"
                  required
                  maxLength={50}
                />
              </div>
            </div>

            {/* Business Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Name (Optional)
              </label>
              <input
                type="text"
                name="business_name"
                value={formData.business_name}
                onChange={handleInputChange}
                className={baseInputClasses}
                placeholder="e.g., Smith Medical Practice, ABC Consulting"
                maxLength={100}
              />
              <p className="text-sm text-gray-500 mt-1">
                Leave blank if this is for personal use
              </p>
            </div>

            {/* Mobile Phone Verification */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Phone Number (Optional)
              </label>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <input
                    type="tel"
                    value={formData.mobile_phone}
                    onChange={handlePhoneChange}
                    className={`flex-1 ${baseInputClasses}`}
                    placeholder="(555) 123-4567"
                    maxLength={14}
                    disabled={mobileVerification.isVerified}
                  />
                  <button
                    type="button"
                    onClick={sendVerificationCode}
                    disabled={!formData.mobile_phone || mobileVerification.isSendingCode || !mobileVerification.canResend || mobileVerification.isVerified}
                    className={`px-4 py-3 font-medium rounded-lg transition-colors duration-200 whitespace-nowrap ${
                      formData.mobile_phone && mobileVerification.canResend && !mobileVerification.isSendingCode && !mobileVerification.isVerified
                        ? 'bg-teal-600 text-white hover:bg-teal-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {mobileVerification.isVerified ? (
                      'Verified'
                    ) : mobileVerification.isSendingCode ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending...
                      </div>
                    ) : mobileVerification.resendTimer > 0 ? (
                      `Resend (${Math.floor(mobileVerification.resendTimer / 60)}:${(mobileVerification.resendTimer % 60).toString().padStart(2, '0')})`
                    ) : mobileVerification.codeSent ? (
                      'Resend Code'
                    ) : (
                      'Send Code'
                    )}
                  </button>
                </div>

                {mobileVerification.codeSent && !mobileVerification.isVerified && (
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={mobileVerification.verificationCode}
                      onChange={(e) => setMobileVerification(prev => ({ ...prev, verificationCode: e.target.value }))}
                      className={`flex-1 ${baseInputClasses}`}
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                    />
                    <button
                      type="button"
                      onClick={verifyCode}
                      disabled={!mobileVerification.verificationCode || mobileVerification.isVerifying}
                      className={`px-4 py-3 font-medium rounded-lg transition-colors duration-200 whitespace-nowrap ${
                        mobileVerification.verificationCode && !mobileVerification.isVerifying
                          ? 'bg-teal-600 text-white hover:bg-teal-700'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {mobileVerification.isVerifying ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Verifying...
                        </div>
                      ) : (
                        'Verify'
                      )}
                    </button>
                  </div>
                )}

                {mobileVerification.isVerified && (
                  <div className="flex items-center text-green-600">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Phone number verified successfully
                  </div>
                )}

                <p className="text-sm text-gray-500">
                  Optional: Provide your mobile number to receive SMS notifications. We'll send you a verification code from +1 848 272 7768 (2PARROT) to confirm your phone number. The code expires in 2 hours.
                </p>
              </div>
            </div>

            {/* Notification Preference */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notification Preference *
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-600">
                  <input
                    type="radio"
                    name="notification_preference"
                    value="email"
                    checked={formData.notification_preference === 'email'}
                    onChange={handleInputChange}
                    className="mr-3 text-teal-600 focus:ring-teal-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">Email</div>
                    <div className="text-sm text-gray-500">Receive notifications via email</div>
                  </div>
                </label>
                <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-600">
                  <input
                    type="radio"
                    name="notification_preference"
                    value="sms"
                    checked={formData.notification_preference === 'sms'}
                    onChange={handleInputChange}
                    className="mr-3 text-teal-600 focus:ring-teal-500"
                    disabled={!mobileVerification.isVerified}
                  />
                  <div>
                    <div className={`font-medium ${!mobileVerification.isVerified ? 'text-gray-400' : 'text-gray-900'}`}>SMS</div>
                    <div className="text-sm text-gray-500">
                      {!mobileVerification.isVerified ? 'Verify phone first' : 'Receive notifications via SMS'}
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Address Fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Street Address *
              </label>
              <input
                type="text"
                name="street_address_1"
                value={formData.street_address_1}
                onChange={handleInputChange}
                className={baseInputClasses}
                placeholder="123 Main Street"
                required
                maxLength={100}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Apartment, Suite, etc. (Optional)
              </label>
              <input
                type="text"
                name="street_address_2"
                value={formData.street_address_2}
                onChange={handleInputChange}
                className={baseInputClasses}
                placeholder="Suite 100"
                maxLength={50}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={baseInputClasses}
                  placeholder="New York"
                  required
                  maxLength={50}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State/Province *
                </label>
                <input
                  type="text"
                  name="state_province"
                  value={formData.state_province}
                  onChange={handleInputChange}
                  className={baseInputClasses}
                  placeholder="NY"
                  required
                  maxLength={50}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country *
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className={baseInputClasses}
                  required
                >
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Australia">Australia</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ZIP/Postal Code *
                </label>
                <input
                  type="text"
                  name="zip_postal_code"
                  value={formData.zip_postal_code}
                  onChange={handleInputChange}
                  className={baseInputClasses}
                  placeholder="10001"
                  required
                  maxLength={20}
                />
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <h4 className="font-medium text-blue-800 mb-1">Why do we need this information?</h4>
                  <p className="text-sm text-blue-700">
                    This information helps us provide better service, comply with regulations, and ensure accurate billing.
                    Your mobile phone number is optional but required if you want to receive SMS notifications.
                    Your data is secure and will never be shared with third parties.
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!validateForm() || isSubmitting}
              className={`w-full py-3 px-6 font-semibold rounded-lg transition-colors duration-200 ${
                validateForm() && !isSubmitting
                  ? 'bg-teal-600 text-white hover:bg-teal-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </div>
              ) : (
                'Continue to Setup →'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-400">
            Fields marked with * are required
          </p>
        </div>
      </div>
    </div>
  );
}


